import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/lib/supabase";
import { env } from "@/lib/env";
import { AuthLayout } from "@/components/Layout";
import { AuthCard } from "@/components/Card";
import { Button } from "@/components/Button";
import { FormField } from "@/components/FormField";
import { Alert } from "@/components/Alert";
import { Logo } from "@/components/Logo";
import { Turnstile } from "@/components/Turnstile";

const schema = z.object({
  first_name: z.string().min(1, "Required").max(80),
  last_name: z.string().min(1, "Required").max(80),
  email: z.string().email("Enter a valid email"),
  password: z.string().min(10, "At least 10 characters").max(128),
});
type FormData = z.infer<typeof schema>;

export default function Signup() {
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  async function onSubmit(values: FormData) {
    setServerError(null);
    if (!captchaToken) {
      setServerError("Please complete the verification challenge.");
      return;
    }
    const { data, error } = await supabase.auth.signUp({
      email: values.email,
      password: values.password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/confirm`,
        data: { first_name: values.first_name, last_name: values.last_name },
        captchaToken,
      },
    });

    if (error) {
      setServerError(error.message);
      return;
    }

    if (!data.session) {
      setSubmitted(true);
      return;
    }

    await supabase.from("patients").insert({
      user_id: data.user!.id,
      first_name: values.first_name,
      last_name: values.last_name,
      phone: null,
    });

    // Best-effort: link any pre-signup Stripe purchase to the new patient
    // row, transfer ownership from any orphan row, and backfill treatments
    // rows from active Stripe subscriptions. Silent failure is OK — the
    // billing page also handles the empty case.
    await supabase.functions.invoke("patient-link-stripe", { body: {} }).then(
      () => null,
      (err: unknown) => console.warn("[Signup] patient-link-stripe failed:", err),
    );

    setSubmitted(true);
  }

  if (submitted) {
    return (
      <AuthLayout>
        <AuthCard>
          <div className="mb-6 flex justify-center"><Logo size="lg" href="https://fortehealth.co" /></div>
          <h1 className="font-serif text-[25px] font-normal text-fh-ink text-center mb-1.5">
            Check your email
          </h1>
          <p className="text-[14px] text-fh-gray text-center">
            We sent a confirmation link to verify your address. Open it on this device to finish
            signing in.
          </p>
          <p className="text-center text-[13px] text-fh-gray mt-6">
            Didn't get it? Check spam, or{" "}
            <Link to="/login" className="font-semibold text-fh-green hover:text-fh-green-dark">
              try signing in
            </Link>.
          </p>
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthCard>
        <div className="mb-6 flex justify-center"><Logo size="lg" href="https://fortehealth.co" /></div>
        <h1 className="font-serif text-[25px] font-normal text-fh-ink text-center mb-1.5">
          Create your account
        </h1>
        <p className="text-[14px] text-fh-gray text-center mb-6">
          Begin your ForteHealth journey.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3.5" noValidate>
          {serverError && <Alert tone="error">{serverError}</Alert>}
          <div className="grid grid-cols-2 gap-3">
            <FormField
              label="First name"
              autoComplete="given-name"
              autoFocus
              error={errors.first_name?.message}
              {...register("first_name")}
            />
            <FormField
              label="Last name"
              autoComplete="family-name"
              error={errors.last_name?.message}
              {...register("last_name")}
            />
          </div>
          <FormField
            label="Email"
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            error={errors.email?.message}
            {...register("email")}
          />
          <FormField
            label="Password"
            type="password"
            autoComplete="new-password"
            hint="Minimum 10 characters."
            error={errors.password?.message}
            {...register("password")}
          />
          <Turnstile siteKey={env.VITE_TURNSTILE_SITE_KEY} onToken={setCaptchaToken} />
          <Button type="submit" loading={isSubmitting} fullWidth className="mt-1">
            Create account
          </Button>
        </form>

        <p className="text-center text-[13px] text-fh-gray mt-[22px]">
          Have an account?{" "}
          <Link to="/login" className="font-semibold text-fh-green hover:text-fh-green-dark">
            Sign in
          </Link>
        </p>
      </AuthCard>
    </AuthLayout>
  );
}
